import ChevronLeftIcon from "@redotech/redo-icons/chevron-left.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import {
  memo,
  useCallback,
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import {
  RedoCheckbox,
  type CheckboxValue,
} from "./arbiter-components/checkbox/redo-checkbox";
import { Divider } from "./arbiter-components/divider/divider";
import * as css from "./drill-down-multi-select.module.css";
import { Flex } from "./flex";
import { Text } from "./text";

export interface DrillDownGroup {
  key: string;
  label: string;
}

export interface DrillDownItem {
  id: string;
  groupKey: string;
  label: string;
  sublabel?: string;
}

export interface RedoDrillDownMultiSelectProps {
  groups: readonly DrillDownGroup[];
  items: readonly DrillDownItem[];
  selectedIds: Set<string>;
  onToggleSingle: (id: string) => void;
  onBulkToggle: (ids: string[], select: boolean) => void;
  /** Singular label for items, used in summary text. Default: "item" */
  itemLabel?: string;
  /** Singular label for groups, used in summary text. Default: "group" */
  groupLabel?: string;
  /** Message shown when a group has no items. */
  emptyGroupMessage?: string;
  helpText?: string;
}

type GroupState = "all" | "some" | "none";

function groupState(
  groupItems: readonly DrillDownItem[],
  selectedIds: Set<string>,
): GroupState {
  const count = groupItems.filter((item) => selectedIds.has(item.id)).length;
  if (count === 0) {
    return "none";
  }
  if (count === groupItems.length) {
    return "all";
  }
  return "some";
}

function toCheckboxValue(state: GroupState): CheckboxValue {
  if (state === "all") {
    return true;
  }
  if (state === "some") {
    return "indeterminate";
  }
  return false;
}

export const RedoDrillDownMultiSelect = memo(function RedoDrillDownMultiSelect({
  groups,
  items,
  selectedIds,
  onToggleSingle,
  onBulkToggle,
  itemLabel = "item",
  groupLabel = "group",
  emptyGroupMessage = "No items in this group yet.",
  helpText,
}: RedoDrillDownMultiSelectProps) {
  const [drillDownKey, setDrillDownKey] = useState<string | null>(null);

  const itemsByGroup = useMemo(() => {
    const map = new Map<string, DrillDownItem[]>();
    for (const item of items) {
      const list = map.get(item.groupKey) ?? [];
      list.push(item);
      map.set(item.groupKey, list);
    }
    return map;
  }, [items]);

  const totalSelected = useMemo(
    () => items.filter((item) => selectedIds.has(item.id)).length,
    [items, selectedIds],
  );

  const groupsWithSelections = useMemo(
    () =>
      groups.filter((group) =>
        (itemsByGroup.get(group.key) ?? []).some((item) =>
          selectedIds.has(item.id),
        ),
      ).length,
    [groups, itemsByGroup, selectedIds],
  );

  const summaryText =
    totalSelected === 0
      ? `No ${itemLabel}s selected`
      : `${totalSelected} ${itemLabel}${totalSelected !== 1 ? "s" : ""} · ${groupsWithSelections} ${groupLabel}${groupsWithSelections !== 1 ? "s" : ""}`;

  const handleToggleGroup = useCallback(
    (groupKey: string) => {
      const groupItems = itemsByGroup.get(groupKey) ?? [];
      const state = groupState(groupItems, selectedIds);
      onBulkToggle(
        groupItems.map((item) => item.id),
        state !== "all",
      );
    },
    [itemsByGroup, selectedIds, onBulkToggle],
  );

  if (drillDownKey !== null) {
    const drillGroup = groups.find((g) => g.key === drillDownKey);
    const drillItems = itemsByGroup.get(drillDownKey) ?? [];

    return (
      <Flex dir="column" gap="sm">
        <Text
          fontSize="xs"
          textColor={totalSelected > 0 ? "primary" : "secondary"}
        >
          {summaryText}
        </Text>
        <Flex
          borderColor="secondary"
          borderRadius="lg"
          borderStyle="solid"
          borderWidth="1px"
          dir="column"
          gap="none"
          overflow="hidden"
        >
          <Flex
            align="center"
            className={css.row}
            cursor="pointer"
            dir="row"
            gap="xs"
            hoverColor="secondary"
            onClick={() => setDrillDownKey(null)}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "Enter" || e.key === " ") {
                setDrillDownKey(null);
              }
            }}
            pl="lg"
            pr="lg"
            role="button"
            tabIndex={0}
          >
            <Flex shrink={0}>
              <ChevronLeftIcon height={16} width={16} />
            </Flex>
            <Text fontSize="sm" fontWeight="medium">
              {drillGroup?.label ?? drillDownKey}
            </Text>
          </Flex>
          {drillItems.length === 0 ? (
            <>
              <Divider />
              <Flex px="lg" py="lg">
                <Text fontSize="xs" textColor="secondary">
                  {emptyGroupMessage}
                </Text>
              </Flex>
            </>
          ) : (
            drillItems.map((item) => {
              const isSelected = selectedIds.has(item.id);
              return (
                <Flex dir="column" gap="none" key={item.id}>
                  <Divider />
                  <Flex
                    align="center"
                    className={css.row}
                    cursor="pointer"
                    dir="row"
                    gap="md"
                    hoverColor="secondary"
                    onClick={() => onToggleSingle(item.id)}
                    onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onToggleSingle(item.id);
                      }
                    }}
                    pl="lg"
                    pr="lg"
                    py="sm"
                    role="button"
                    tabIndex={0}
                  >
                    <Flex
                      onClick={(e: MouseEvent) => e.stopPropagation()}
                      shrink={0}
                    >
                      <RedoCheckbox
                        ariaLabel={item.label}
                        setValue={() => onToggleSingle(item.id)}
                        size="sm"
                        value={isSelected}
                      />
                    </Flex>
                    <Flex dir="column" gap="none">
                      <Text fontSize="sm" fontWeight="medium">
                        {item.label}
                      </Text>
                      {item.sublabel && (
                        <Text fontSize="xs" textColor="secondary">
                          {item.sublabel}
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                </Flex>
              );
            })
          )}
        </Flex>
        {helpText && (
          <Text fontSize="xs" textColor="secondary">
            {helpText}
          </Text>
        )}
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="sm">
      <Text
        fontSize="xs"
        textColor={totalSelected > 0 ? "primary" : "secondary"}
      >
        {summaryText}
      </Text>
      <Flex
        borderColor="secondary"
        borderRadius="lg"
        borderStyle="solid"
        borderWidth="1px"
        dir="column"
        gap="none"
        overflow="hidden"
      >
        {groups.map((group, index) => {
          const groupItems = itemsByGroup.get(group.key) ?? [];
          const state = groupState(groupItems, selectedIds);
          const selectedCount = groupItems.filter((item) =>
            selectedIds.has(item.id),
          ).length;

          return (
            <Flex dir="column" gap="none" key={group.key}>
              {index > 0 && <Divider />}
              <Flex
                align="center"
                className={css.row}
                dir="row"
                justify="space-between"
              >
                <Flex
                  align="center"
                  alignSelf="stretch"
                  cursor="pointer"
                  dir="row"
                  gap="md"
                  grow="1"
                  hoverColor="secondary"
                  onClick={() => handleToggleGroup(group.key)}
                  onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === "Enter" || e.key === " ") {
                      handleToggleGroup(group.key);
                    }
                  }}
                  pl="lg"
                  pr="md"
                  role="button"
                  tabIndex={0}
                >
                  <Flex
                    onClick={(e: MouseEvent) => e.stopPropagation()}
                    shrink={0}
                  >
                    <RedoCheckbox
                      ariaLabel={group.label}
                      setValue={() => handleToggleGroup(group.key)}
                      size="sm"
                      value={toCheckboxValue(state)}
                    />
                  </Flex>
                  <Text fontSize="sm" fontWeight="medium">
                    {group.label}
                  </Text>
                </Flex>
                <Flex align="center" dir="row" gap="sm" pr="md" shrink={0}>
                  <Text fontSize="xs" textColor="secondary">
                    {selectedCount}/{groupItems.length}
                  </Text>
                  <Flex
                    align="center"
                    cursor="pointer"
                    justify="center"
                    onClick={(e: MouseEvent) => {
                      e.stopPropagation();
                      setDrillDownKey(group.key);
                    }}
                    onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        setDrillDownKey(group.key);
                      }
                    }}
                    p="xs"
                    role="button"
                    tabIndex={0}
                  >
                    <Flex shrink={0}>
                      <ChevronRightIcon height={16} width={16} />
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
      {helpText && (
        <Text fontSize="xs" textColor="secondary">
          {helpText}
        </Text>
      )}
    </Flex>
  );
});
